import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@voicely/ui-mui/feedback/Dialog";
import { Button } from "@voicely/ui-mui/form/Button";
import { MenuItem } from "@voicely/ui-mui/form/MenuItem";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useState } from "react";
import { toast } from "react-toastify";

interface DialogProps {
	onClose: () => void;
}

/**
 * Give a workspace a number, pointed at one of its agents.
 *
 * The agent list is filtered to the chosen workspace on the client for the
 * menu's sake only; the server re-checks that the agent belongs to it, because
 * a number is an authorization input and the check cannot live in a dropdown.
 */
export const AssignNumberDialog = ({ onClose }: DialogProps) => {
	const queryClient = useQueryClient();
	const teamsQuery = useQuery(orpc.superAdmin.listTeams.queryOptions());
	const agentsQuery = useQuery(orpc.superAdmin.listAgents.queryOptions());

	const [teamId, setTeamId] = useState("");
	const [agentId, setAgentId] = useState("");
	const [e164, setE164] = useState("");
	const [label, setLabel] = useState("");

	const teamAgents = (agentsQuery.data ?? []).filter((agent) => agent.teamId === teamId);

	const assign = useMutation(
		orpc.telephony.adminMapNumber.mutationOptions({
			onSuccess: async (row) => {
				toast.success(`+${row.e164} assigned.`);
				await queryClient.invalidateQueries({ queryKey: orpc.superAdmin.listPhoneNumbers.key() });
				onClose();
			},
		}),
	);

	const error = assign.error as { message?: string } | null;

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Assign a number to a workspace</DialogTitle>
			<DialogContent>
				<Stack
					component="form"
					id="assign-number-form"
					spacing={2}
					sx={{ pt: 1 }}
					onSubmit={(event) => {
						event.preventDefault();
						assign.mutate({
							teamId,
							agentId,
							e164: e164.trim(),
							label: label.trim() || undefined,
						});
					}}
				>
					{error?.message && <Alert severity="error">{error.message}</Alert>}
					<TextField
						select
						size="small"
						label="Workspace"
						value={teamId}
						onChange={(e) => {
							setTeamId(e.target.value);
							setAgentId("");
						}}
						required
					>
						{(teamsQuery.data ?? []).map((team) => (
							<MenuItem key={team.id} value={team.id}>
								{team.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						size="small"
						label="Number"
						placeholder="+91 96247 05678"
						helperText="Any format; stored as the digits the carrier sends. Must already be on the carrier account."
						value={e164}
						onChange={(e) => setE164(e.target.value)}
						required
					/>
					<TextField
						select
						size="small"
						label="Agent that answers inbound calls"
						value={agentId}
						onChange={(e) => setAgentId(e.target.value)}
						disabled={!teamId}
						helperText={
							teamId && teamAgents.length === 0
								? "This workspace has no agents yet. Create one first."
								: undefined
						}
						required
					>
						{teamAgents.map((agent) => (
							<MenuItem key={agent.id} value={agent.id}>
								{agent.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						size="small"
						label="Label (optional)"
						value={label}
						onChange={(e) => setLabel(e.target.value)}
					/>
					<Typography variant="caption" color="text.secondary">
						Once a workspace has a number it can run batch calls. Without one it can only place
						single test calls from the shared caller ID.
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					type="submit"
					form="assign-number-form"
					variant="contained"
					disabled={assign.isPending || !teamId || !agentId || e164.trim() === ""}
				>
					Assign
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/**
 * Override the shared outbound caller ID, or hand it back to the environment.
 */
export const DefaultCallerIdDialog = ({ onClose }: DialogProps) => {
	const queryClient = useQueryClient();
	const stateQuery = useQuery(orpc.superAdmin.defaultCallerId.queryOptions());
	const [e164, setE164] = useState("");

	const save = useMutation(
		orpc.telephony.adminSetDefaultCallerId.mutationOptions({
			onSuccess: async (state) => {
				toast.success(
					state.override ? `Shared caller ID is now +${state.override}.` : "Override cleared.",
				);
				await queryClient.invalidateQueries({ queryKey: orpc.superAdmin.defaultCallerId.key() });
				await queryClient.invalidateQueries({ queryKey: orpc.superAdmin.listPhoneNumbers.key() });
				onClose();
			},
		}),
	);

	const state = stateQuery.data;
	const error = save.error as { message?: string } | null;

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Shared outbound caller ID</DialogTitle>
			<DialogContent>
				<Stack
					component="form"
					id="default-caller-form"
					spacing={2}
					sx={{ pt: 1 }}
					onSubmit={(event) => {
						event.preventDefault();
						save.mutate({ e164: e164.trim() });
					}}
				>
					{error?.message && <Alert severity="error">{error.message}</Alert>}
					<Typography variant="body2" color="text.secondary">
						The number single test calls present when a workspace names none. It takes effect within
						seconds on every replica. The carrier must already have it on the account, or every test
						call is rejected.
					</Typography>
					{state && (
						<Typography variant="body2">
							In force: <strong>{state.effective ? `+${state.effective}` : "none"}</strong>
							{state.override ? " (override)" : " (from the environment)"}
							{state.override && state.fromEnv ? ` · environment value: +${state.fromEnv}` : ""}
						</Typography>
					)}
					<TextField
						size="small"
						label="New shared caller ID"
						placeholder="+91 80653 54620"
						value={e164}
						onChange={(e) => setE164(e.target.value)}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				{state?.override && (
					<Button
						color="warning"
						disabled={save.isPending}
						onClick={() => save.mutate({ e164: null })}
					>
						Use the environment value
					</Button>
				)}
				<Button
					type="submit"
					form="default-caller-form"
					variant="contained"
					disabled={save.isPending || e164.trim() === ""}
				>
					Set override
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/**
 * Which workspaces may run batch calls on the shared outbound caller ID.
 *
 * Refused by default: the shared ID is one number and one channel pool for
 * every workspace, so a campaign on it spends the platform's number on one
 * customer. Allowing a workspace here is the explicit exception, one workspace
 * at a time. It is not a number assigned to the workspace, so it does not
 * appear in the numbers table and gives the workspace no inbound line.
 *
 * Revoking stops the workspace's NEXT launch; a batch already running keeps
 * dialling until it finishes or is stopped.
 */
export const SharedLineBatchesDialog = ({ onClose }: DialogProps) => {
	const queryClient = useQueryClient();
	const teamsQuery = useQuery(orpc.superAdmin.listTeams.queryOptions());
	const grantsQuery = useQuery(orpc.superAdmin.sharedLineBatchGrants.queryOptions());
	const callerQuery = useQuery(orpc.superAdmin.defaultCallerId.queryOptions());
	const [teamId, setTeamId] = useState("");

	const set = useMutation(
		orpc.telephony.adminSetSharedLineBatches.mutationOptions({
			onSuccess: async (row) => {
				toast.success(
					row
						? `${row.teamName ?? "Workspace"} may now run batches on the shared number.`
						: "Revoked.",
				);
				setTeamId("");
				await queryClient.invalidateQueries({
					queryKey: orpc.superAdmin.sharedLineBatchGrants.key(),
				});
			},
		}),
	);

	const grants = grantsQuery.data ?? [];
	const granted = new Set(grants.map((grant) => grant.teamId));
	const candidates = (teamsQuery.data ?? []).filter((team) => !granted.has(team.id));
	const shared = callerQuery.data?.effective;
	const error = set.error as { message?: string } | null;

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Batches on the shared caller ID</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					{error?.message && <Alert severity="error">{error.message}</Alert>}
					<Typography variant="body2" color="text.secondary">
						Batch calls are refused on the shared caller ID
						{shared ? ` (+${shared})` : ""}, because every workspace shares that one number and one
						channel pool. Allowing a workspace here is the exception, one at a time. Revoking stops
						its next launch; a batch already running keeps dialling until it finishes or is stopped.
					</Typography>
					{!shared && (
						<Alert severity="warning">
							No shared caller ID is configured, so nobody can be allowed until one is set.
						</Alert>
					)}

					{grants.length === 0 ? (
						<Typography variant="body2">No workspace is allowed yet.</Typography>
					) : (
						<Stack spacing={1}>
							{grants.map((grant) => (
								<Stack
									key={grant.teamId}
									direction="row"
									alignItems="center"
									justifyContent="space-between"
								>
									<Typography variant="body2">{grant.teamName ?? grant.teamId}</Typography>
									<Button
										color="warning"
										size="small"
										disabled={set.isPending}
										onClick={() => set.mutate({ teamId: grant.teamId, allowed: false })}
									>
										Revoke
									</Button>
								</Stack>
							))}
						</Stack>
					)}

					<Stack direction="row" spacing={1} alignItems="center">
						<TextField
							select
							size="small"
							fullWidth
							label="Allow another workspace"
							value={teamId}
							onChange={(e) => setTeamId(e.target.value)}
						>
							{candidates.map((team) => (
								<MenuItem key={team.id} value={team.id}>
									{team.name}
								</MenuItem>
							))}
						</TextField>
						<Button
							variant="contained"
							disabled={set.isPending || teamId === "" || !shared}
							onClick={() => set.mutate({ teamId, allowed: true })}
						>
							Allow
						</Button>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
